import React, { useState, useEffect, useRef, useCallback } from 'react';
import './EditableArticleViewer.css';

interface EditableArticleViewerProps {
  markdownContent: string;
  // May return `false` (sync or via Promise) to signal a refused save (e.g.
  // ContentPreview's wrapper guard) — the caller must not clear its dirty
  // state in that case. Returning void/true/undefined means "saved".
  onSave: (updatedMarkdown: string) => void | boolean | Promise<void | boolean>;
  onCancel: () => void;
}

interface Block {
  id: string;
  type: 'h1' | 'h2' | 'h3' | 'p' | 'ul' | 'ol' | 'blockquote' | 'code' | 'table';
  content: string;
}

const EditableArticleViewer: React.FC<EditableArticleViewerProps> = ({
  markdownContent,
  onSave,
  onCancel
}) => {
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  // wrapperKey names the exact key the editable content came from, so save can
  // re-wrap into that same key. null means "no wrapper" — the edited markdown is
  // the whole value, saved verbatim. Previously a boolean `isJSON` conflated
  // "parsed as JSON" with "has a wrapper key", so a JSON payload with NONE of the
  // three recognized keys (e.g. a meta_data task's {seo_title, slug, ...}) still
  // set isJSON=true/wrapper=parsed, and handleSave's re-wrap silently discarded
  // the user's edit by re-serializing the original wrapper unchanged (Phase 0 fix).
  const originalFormatRef = useRef<{ wrapperKey: 'response' | 'result' | 'content' | null; wrapper: any }>({ wrapperKey: null, wrapper: null });
  // Track live edits in a ref to avoid re-rendering contentEditable on every keystroke
  const liveBlockContentRef = useRef<Record<string, string>>({});

  // Extract content from JSON if needed, then parse into blocks
  useEffect(() => {
    const { content: extractedContent, wrapperKey, wrapper } = extractContentFromJSON(markdownContent);
    originalFormatRef.current = { wrapperKey, wrapper };
    const parsed = parseMarkdownToBlocks(extractedContent);
    setBlocks(parsed);
  }, [markdownContent]);

  // Extract markdown content from JSON wrapper if present
  const extractContentFromJSON = (content: string): { content: string; wrapperKey: 'response' | 'result' | 'content' | null; wrapper: any } => {
    const trimmed = content.trim();

    // Try to parse as JSON
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        // Extract content from common JSON structures
        if (parsed.response && typeof parsed.response === 'string') {
          return { content: parsed.response, wrapperKey: 'response', wrapper: parsed };
        }
        if (parsed.result && typeof parsed.result === 'string') {
          return { content: parsed.result, wrapperKey: 'result', wrapper: parsed };
        }
        if (parsed.content && typeof parsed.content === 'string') {
          return { content: parsed.content, wrapperKey: 'content', wrapper: parsed };
        }
        // Parsed as JSON but none of the recognized keys are present (e.g. a
        // meta_data payload like {seo_title, slug, ...}) — there is nowhere safe
        // to write an edit back into, so this is NOT treated as wrapped. The
        // whole object still renders as markdown blocks (honest limitation, not
        // silently discarding structured fields) and a save writes exactly what
        // the user edited, verbatim.
        return { content, wrapperKey: null, wrapper: null };
      } catch (e) {
        // Not valid JSON, treat as plain text
      }
    }

    // NOTE: a Python-dict-repr branch (single-quote-specific regex on
    // {'response': '...', 'stats': {...}}) used to live here. It's removed now
    // that ContentPreview.tsx feeds this component the backend-unwrapped
    // synthesized_result_clean (content-editor-synthesized-unwrap fix, 2026-09)
    // — markdownContent never arrives wrapped anymore for a task fetched via
    // GET /api/tasks/{id}. Kept as a JSON-only extractor for a real payload
    // (e.g. a meta_data task's structured JSON) and as the plain-text fallback.

    // Return as-is if not JSON
    return { content, wrapperKey: null, wrapper: null };
  };

  // Parse markdown into editable blocks
  const parseMarkdownToBlocks = (md: string): Block[] => {
    const lines = md.split('\n');
    const result: Block[] = [];
    let currentBlock: Block | null = null;
    let codeBuffer: string[] = [];
    let inCodeBlock = false;
    let codeLanguage = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();

      // Code block handling
      if (trimmed.startsWith('```')) {
        if (inCodeBlock) {
          // End code block
          result.push({
            id: `block-${result.length}`,
            type: 'code',
            content: codeBuffer.join('\n')
          });
          codeBuffer = [];
          inCodeBlock = false;
        } else {
          // Start code block
          if (currentBlock) {
            result.push(currentBlock);
            currentBlock = null;
          }
          inCodeBlock = true;
          codeLanguage = trimmed.slice(3).trim();
        }
        continue;
      }

      if (inCodeBlock) {
        codeBuffer.push(line);
        continue;
      }

      // Headers
      if (trimmed.startsWith('# ')) {
        if (currentBlock) {
          result.push(currentBlock);
        }
        result.push({
          id: `block-${result.length}`,
          type: 'h1',
          content: trimmed.slice(2)
        });
        currentBlock = null;
        continue;
      }

      if (trimmed.startsWith('## ')) {
        if (currentBlock) {
          result.push(currentBlock);
        }
        result.push({
          id: `block-${result.length}`,
          type: 'h2',
          content: trimmed.slice(3)
        });
        currentBlock = null;
        continue;
      }

      if (trimmed.startsWith('### ')) {
        if (currentBlock) {
          result.push(currentBlock);
        }
        result.push({
          id: `block-${result.length}`,
          type: 'h3',
          content: trimmed.slice(4)
        });
        currentBlock = null;
        continue;
      }

      // Blockquote
      if (trimmed.startsWith('> ')) {
        if (currentBlock && currentBlock.type !== 'blockquote') {
          result.push(currentBlock);
          currentBlock = null;
        }
        if (!currentBlock) {
          currentBlock = {
            id: `block-${result.length}`,
            type: 'blockquote',
            content: ''
          };
        }
        currentBlock.content += (currentBlock.content ? '\n' : '') + trimmed.slice(2);
        continue;
      }

      // Lists
      if (trimmed.match(/^[-*] /)) {
        if (currentBlock && currentBlock.type !== 'ul') {
          result.push(currentBlock);
          currentBlock = null;
        }
        if (!currentBlock) {
          currentBlock = {
            id: `block-${result.length}`,
            type: 'ul',
            content: ''
          };
        }
        currentBlock.content += (currentBlock.content ? '\n' : '') + trimmed.slice(2);
        continue;
      }

      if (trimmed.match(/^\d+\. /)) {
        if (currentBlock && currentBlock.type !== 'ol') {
          result.push(currentBlock);
          currentBlock = null;
        }
        if (!currentBlock) {
          currentBlock = {
            id: `block-${result.length}`,
            type: 'ol',
            content: ''
          };
        }
        currentBlock.content += (currentBlock.content ? '\n' : '') + trimmed.replace(/^\d+\. /, '');
        continue;
      }

      // Table: a pipe row followed by a separator row (| --- | --- |). Captured
      // verbatim as its own block so it survives the markdown round-trip on save —
      // without this the rows fall through to the paragraph branch below and get
      // joined with spaces, destroying the table.
      const isTableRow = (l: string) => {
        const t = l.trim();
        return t.startsWith('|') && (t.match(/\|/g) || []).length >= 2;
      };
      const isTableSep = (l: string) => {
        const t = l.trim();
        return t.includes('-') && t.includes('|') && /^[\s|:-]+$/.test(t);
      };
      if (isTableRow(line) && i + 1 < lines.length && isTableSep(lines[i + 1])) {
        if (currentBlock) {
          result.push(currentBlock);
          currentBlock = null;
        }
        const tableLines: string[] = [line];
        let j = i + 1;
        while (j < lines.length && lines[j].trim() !== '' && lines[j].includes('|')) {
          tableLines.push(lines[j]);
          j++;
        }
        result.push({
          id: `block-${result.length}`,
          type: 'table',
          content: tableLines.join('\n'),
        });
        i = j - 1;
        continue;
      }

      // Empty line - close current block
      if (trimmed === '') {
        if (currentBlock) {
          result.push(currentBlock);
          currentBlock = null;
        }
        continue;
      }

      // Regular paragraph
      if (currentBlock && currentBlock.type !== 'p') {
        result.push(currentBlock);
        currentBlock = null;
      }
      if (!currentBlock) {
        currentBlock = {
          id: `block-${result.length}`,
          type: 'p',
          content: ''
        };
      }
      currentBlock.content += (currentBlock.content ? ' ' : '') + line;
    }

    // Don't forget the last block
    if (currentBlock) {
      result.push(currentBlock);
    }

    return result;
  };

  // Convert blocks back to markdown
  const blocksToMarkdown = (blocks: Block[]): string => {
    return blocks.map(block => {
      switch (block.type) {
        case 'h1':
          return `# ${block.content}`;
        case 'h2':
          return `## ${block.content}`;
        case 'h3':
          return `### ${block.content}`;
        case 'blockquote':
          return block.content.split('\n').map(line => `> ${line}`).join('\n');
        case 'ul':
          return block.content.split('\n').map(line => `- ${line}`).join('\n');
        case 'ol':
          return block.content.split('\n').map((line, i) => `${i + 1}. ${line}`).join('\n');
        case 'code':
          return `\`\`\`\n${block.content}\n\`\`\``;
        case 'table':
          return block.content;
        case 'p':
        default:
          return block.content;
      }
    }).join('\n\n');
  };

  // Track live edits in ref (no re-render) — called on every input
  const handleBlockInput = (blockId: string, newContent: string) => {
    liveBlockContentRef.current[blockId] = newContent;
    setHasChanges(true);
  };

  // Commit live edits to React state — called on blur
  const commitBlockChange = (blockId: string, newContent: string) => {
    liveBlockContentRef.current[blockId] = newContent;
    setBlocks(prev => prev.map(block =>
      block.id === blockId ? { ...block, content: newContent } : block
    ));
    setHasChanges(true);
  };

  // Change block type
  const changeBlockType = (blockId: string, newType: Block['type']) => {
    setBlocks(prev => prev.map(block =>
      block.id === blockId ? { ...block, type: newType } : block
    ));
    setHasChanges(true);
  };

  // Add new block after current
  const addBlockAfter = (blockId: string) => {
    const index = blocks.findIndex(b => b.id === blockId);
    const newBlock: Block = {
      id: `block-${Date.now()}`,
      type: 'p',
      content: ''
    };
    const newBlocks = [...blocks];
    newBlocks.splice(index + 1, 0, newBlock);
    setBlocks(newBlocks);
    setHasChanges(true);
    // Focus the new block
    setTimeout(() => {
      const newElement = document.querySelector(`[data-block-id="${newBlock.id}"]`) as HTMLElement;
      if (newElement) {
        newElement.focus();
        setSelectedBlockId(newBlock.id);
      }
    }, 0);
  };

  // Delete block
  const deleteBlock = (blockId: string) => {
    if (blocks.length <= 1) return; // Keep at least one block
    setBlocks(prev => prev.filter(b => b.id !== blockId));
    setHasChanges(true);
  };

  // Handle key presses
  const handleKeyDown = (e: React.KeyboardEvent, blockId: string) => {
    // Enter to create new block
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Flush current content before adding new block
      const currentText = (e.currentTarget as HTMLElement).innerText;
      commitBlockChange(blockId, currentText);
      addBlockAfter(blockId);
    }

    // Backspace on empty block to delete
    if (e.key === 'Backspace' && (e.currentTarget as HTMLElement).innerText === '') {
      e.preventDefault();
      deleteBlock(blockId);
    }
  };

  // Apply formatting
  const applyFormat = (command: string) => {
    document.execCommand(command, false);
    setHasChanges(true);
  };

  // Render a markdown table block as a read-only HTML table. It is NOT
  // contentEditable, so the editor's innerText-on-blur can't flatten it — the
  // block's raw markdown (block.content) is preserved verbatim through save.
  const renderMarkdownTable = (md: string) => {
    const rows = md.split('\n').map(r => r.trim()).filter(Boolean);
    if (rows.length < 2) return <pre>{md}</pre>;
    const stripInline = (s: string) => s.replace(/\*\*/g, '').replace(/`/g, '');
    const cells = (row: string) =>
      row.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => stripInline(c.trim()));
    const header = cells(rows[0]);
    const body = rows.slice(2).map(cells);
    return (
      <table className="article-edit-table">
        <thead>
          <tr>{header.map((c, i) => <th key={i}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {body.map((r, ri) => (
            <tr key={ri}>{r.map((c, ci) => <td key={ci}>{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    // Flush any pending live edits from the ref into blocks
    const flushedBlocks = blocks.map(block => {
      const live = liveBlockContentRef.current[block.id];
      return live !== undefined ? { ...block, content: live } : block;
    });
    let markdown = blocksToMarkdown(flushedBlocks);

    // Wrap back in JSON ONLY when the content actually came from a recognized
    // wrapper key — re-wrapping unconditionally on "was JSON" let a payload with
    // none of the three keys silently discard the user's edit by re-serializing
    // the original wrapper unchanged (Phase 0 fix). wrapperKey === null means the
    // edited markdown IS the value; save it verbatim.
    const { wrapperKey, wrapper } = originalFormatRef.current;
    if (wrapperKey !== null && wrapper) {
      const rewrapped = { ...wrapper, [wrapperKey]: markdown };
      markdown = JSON.stringify(rewrapped);
    }

    const result = await onSave(markdown);
    setIsSaving(false);
    // A refused save (e.g. ContentPreview's wrapper guard) returns `false` —
    // leave hasChanges set so the editor still shows unsaved changes.
    if (result === false) {
      return;
    }
    setHasChanges(false);
  };

  // Get selected block type
  const selectedBlock = blocks.find(b => b.id === selectedBlockId);

  // Render block content with inline formatting
  const renderBlockContent = (block: Block) => {
    if (block.type === 'ul') {
      return (
        <ul>
          {block.content.split('\n').map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    }
    if (block.type === 'ol') {
      return (
        <ol>
          {block.content.split('\n').map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      );
    }
    if (block.type === 'code') {
      return <code>{block.content}</code>;
    }
    return block.content;
  };

  return (
    <div className="editable-article-viewer">
      {/* Floating Toolbar */}
      {selectedBlockId && (
        <div className="floating-toolbar">
          <div className="toolbar-section">
            <button
              className={`toolbar-btn ${selectedBlock?.type === 'h1' ? 'active' : ''}`}
              onClick={() => changeBlockType(selectedBlockId, 'h1')}
              title="Heading 1"
            >
              H1
            </button>
            <button
              className={`toolbar-btn ${selectedBlock?.type === 'h2' ? 'active' : ''}`}
              onClick={() => changeBlockType(selectedBlockId, 'h2')}
              title="Heading 2"
            >
              H2
            </button>
            <button
              className={`toolbar-btn ${selectedBlock?.type === 'h3' ? 'active' : ''}`}
              onClick={() => changeBlockType(selectedBlockId, 'h3')}
              title="Heading 3"
            >
              H3
            </button>
          </div>

          <div className="toolbar-section">
            <button
              className={`toolbar-btn ${selectedBlock?.type === 'p' ? 'active' : ''}`}
              onClick={() => changeBlockType(selectedBlockId, 'p')}
              title="Paragraph"
            >
              ¶
            </button>
            <button
              className={`toolbar-btn ${selectedBlock?.type === 'ul' ? 'active' : ''}`}
              onClick={() => changeBlockType(selectedBlockId, 'ul')}
              title="Bullet List"
            >
              •
            </button>
            <button
              className={`toolbar-btn ${selectedBlock?.type === 'ol' ? 'active' : ''}`}
              onClick={() => changeBlockType(selectedBlockId, 'ol')}
              title="Numbered List"
            >
              1.
            </button>
            <button
              className={`toolbar-btn ${selectedBlock?.type === 'blockquote' ? 'active' : ''}`}
              onClick={() => changeBlockType(selectedBlockId, 'blockquote')}
              title="Quote"
            >
              "
            </button>
          </div>

          <div className="toolbar-section">
            <button
              className="toolbar-btn"
              onClick={() => applyFormat('bold')}
              title="Bold"
            >
              <strong>B</strong>
            </button>
            <button
              className="toolbar-btn"
              onClick={() => applyFormat('italic')}
              title="Italic"
            >
              <em>I</em>
            </button>
          </div>

          <div className="toolbar-section">
            <button
              className="toolbar-btn"
              onClick={() => addBlockAfter(selectedBlockId)}
              title="Add Block"
            >
              +
            </button>
          </div>
        </div>
      )}

      {/* Editable Content */}
      <div className="editable-content" ref={contentRef}>
        {blocks.map((block) => (
          block.type === 'table' ? (
            <div
              key={block.id}
              data-block-id={block.id}
              className="editable-block table-block"
              data-type="table"
            >
              {renderMarkdownTable(block.content)}
            </div>
          ) : (
          <div
            key={block.id}
            data-block-id={block.id}
            className={`editable-block ${selectedBlockId === block.id ? 'editing' : ''}`}
            data-type={block.type}
            contentEditable
            suppressContentEditableWarning
            onFocus={() => setSelectedBlockId(block.id)}
            onBlur={(e) => {
              commitBlockChange(block.id, e.currentTarget.innerText);
              setTimeout(() => setSelectedBlockId(null), 200);
            }}
            onKeyDown={(e) => handleKeyDown(e, block.id)}
            onInput={(e) => handleBlockInput(block.id, e.currentTarget.innerText)}
          >
            {block.type === 'ul' ? (
              <ul>
                {block.content.split('\n').map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            ) : block.type === 'ol' ? (
              <ol>
                {block.content.split('\n').map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ol>
            ) : block.type === 'code' ? (
              <pre><code>{block.content}</code></pre>
            ) : (
              block.content
            )}
          </div>
          )
        ))}
      </div>

      {/* Bottom Action Bar */}
      <div className="edit-actions-bar">
        <button
          className="cancel-btn"
          onClick={onCancel}
          disabled={isSaving}
        >
          ❌ Cancel
        </button>
        {hasChanges && (
          <span style={{ color: '#e53e3e', fontSize: '14px' }}>
            Unsaved changes
          </span>
        )}
        <button
          className="save-btn"
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
        >
          {isSaving ? 'Saving...' : '💾 Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default EditableArticleViewer;
